/**
 * Background Dialog - Handles background info functionality (now in RAG dialog tab)
 */

import { appendMessage } from "../utils/helpers.js";
import { debugError } from "../config/debugConfig.js";
import { isExcelHost } from "../config/hostConfig.js";

let backgroundInfo = "";
let columnBackgroundInfo = {};
let currentColumnId = null;
let currentColumnName = null;

// Getters for other modules
export function getBackgroundInfo() {
  return backgroundInfo;
}

export function getColumnBackgroundInfo() {
  return columnBackgroundInfo;
}

export function loadBackgroundInfo() {
  // Load legacy single background info (for migration)
  const savedInfo = localStorage.getItem("backgroundInfo");
  if (savedInfo) {
    backgroundInfo = savedInfo;
  }
  
  // Load column-specific background info
  const savedColumnInfo = localStorage.getItem("columnBackgroundInfo");
  if (savedColumnInfo) {
    try {
      columnBackgroundInfo = JSON.parse(savedColumnInfo);
    } catch (error) {
      debugError("Error parsing column background info:", error);
      columnBackgroundInfo = {};
    }
  }
}

/**
 * Initialize the background tab when RAG dialog opens
 */
export async function initBackgroundTab() {
  if (!isExcelHost()) {
    initGlobalBackgroundTab();
    return;
  }
  try {
    await Excel.run(async (context) => {
      const range = context.workbook.getSelectedRange();
      const sheet = range.worksheet;
      
      // Check if this is a parts data sheet by reading cell A1
      const cellA1 = sheet.getRange("A1");
      range.load("columnIndex");
      cellA1.load("values");
      await context.sync();
      
      if (cellA1.values[0][0] === "part_P_ARTICLE_PARTNR") {
        const firstColIndex = range.columnIndex;
      
        // Get column ID from row 1 (index 0)
        const columnIdCell = sheet.getRangeByIndexes(0, firstColIndex, 1, 1);
        columnIdCell.load("values");
        
        // Get column name from row 3 (index 2)
        const columnNameCell = sheet.getRangeByIndexes(2, firstColIndex, 1, 1);
        columnNameCell.load("values");
        
        await context.sync();
        
        currentColumnId = columnIdCell.values[0][0];
        currentColumnName = columnNameCell.values[0][0];
        
        // Load existing background info for this column
        const textarea = document.getElementById("background-text");
        const columnInfoDiv = document.getElementById("column-info");
        
        // Show import/export buttons for parts data
        document.getElementById("import-export-group").style.display = "flex";
        
        // Display column info
        if (columnInfoDiv) {
          columnInfoDiv.textContent = `Eigenschaft: ${currentColumnName}`;
        }
        
        // Load saved info for this column, or fall back to legacy global info
        textarea.value = columnBackgroundInfo[currentColumnId] || backgroundInfo || "";
      } else {
        initGlobalBackgroundTab();
      }
    });
  } catch (error) {
    debugError("Error initializing background tab:", error);
    initGlobalBackgroundTab();
  }
}

function initGlobalBackgroundTab() {
  const textarea = document.getElementById("background-text");
  const columnInfoDiv = document.getElementById("column-info");
  
  // Hide import/export buttons for non-parts data
  document.getElementById("import-export-group").style.display = "none";
  
  currentColumnId = null;
  currentColumnName = null;
  
  if (columnInfoDiv) {
    columnInfoDiv.textContent = "Allgemeine Hintergrundinformationen";
  }
  
  textarea.value = backgroundInfo;
}

export function saveBackgroundInfo() {
  const textarea = document.getElementById("background-text");
  const infoText = textarea.value;
  
  if (currentColumnId) {
    if ((columnBackgroundInfo[currentColumnId] || "") !== (infoText || "")) {
      // Save for specific column
      if (infoText.trim()) {
        columnBackgroundInfo[currentColumnId] = infoText;
      } else {
        delete columnBackgroundInfo[currentColumnId];
      }
      
      localStorage.setItem("columnBackgroundInfo", JSON.stringify(columnBackgroundInfo));

      if (infoText.trim()) {
        appendMessage(`✅ Hintergrundinformationen für Eigenschaft "${currentColumnName}" wurden gespeichert.`, false);
      } else {
        appendMessage(`ℹ️ Hintergrundinformationen für Eigenschaft "${currentColumnName}" wurden entfernt.`, false);
      }
    }
  } else {
    if ((backgroundInfo || "") !== (infoText || "")) {
      backgroundInfo = infoText;
      localStorage.setItem("backgroundInfo", backgroundInfo);
      if (infoText.trim()) {
        appendMessage("✅ Allgemeine Hintergrundinformationen wurden gespeichert.", false);
      } else {
        appendMessage("ℹ️ Allgemeine Hintergrundinformationen wurden entfernt.", false);
      }
    }
  }
}

export async function reloadBackgroundDialog() {
  if (!isExcelHost()) return;
  try {
    await Excel.run(async (context) => {
      const range = context.workbook.getSelectedRange();
      const sheet = range.worksheet;
      
      const cellA1 = sheet.getRange("A1");
      range.load("columnIndex");
      cellA1.load("values");
      await context.sync();
      
      if (cellA1.values[0][0] === "part_P_ARTICLE_PARTNR") {
        const firstColIndex = range.columnIndex;
      
        const columnIdCell = sheet.getRangeByIndexes(0, firstColIndex, 1, 1);
        columnIdCell.load("values");
        
        const columnNameCell = sheet.getRangeByIndexes(2, firstColIndex, 1, 1);
        columnNameCell.load("values");
        
        await context.sync();
        
        currentColumnId = columnIdCell.values[0][0];
        currentColumnName = columnNameCell.values[0][0];
        
        const columnInfoDiv = document.getElementById("column-info");
        if (columnInfoDiv) {
          columnInfoDiv.textContent = `Eigenschaft: ${currentColumnName}`;
        }
        
        const textarea = document.getElementById("background-text");
        textarea.value = columnBackgroundInfo[currentColumnId] || "";
      } else {
        const columnInfoDiv = document.getElementById("column-info");
        if (columnInfoDiv) {
          columnInfoDiv.textContent = "Allgemeine Hintergrundinformationen";
        }
        
        const textarea = document.getElementById("background-text");
        textarea.value = backgroundInfo;
      }
    });
  } catch (error) {
    debugError("Error reloading background dialog:", error);
  }
}

export async function loadBackgroundFromRow4(showConfirmDialog) {
  showConfirmDialog(
    "Möchten Sie wirklich die Hintergrundinformationen aus Zeile 4 importieren? Bestehende Informationen werden überschrieben.",
    () => performLoadBackgroundFromRow4()
  );
}

async function performLoadBackgroundFromRow4() {
  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      
      const columnIdsRange = sheet.getRangeByIndexes(0, 0, 1, 100);
      columnIdsRange.load("values");
      
      const row4Range = sheet.getRangeByIndexes(3, 0, 1, 100);
      row4Range.load("values");
      
      await context.sync();
      
      const columnIds = columnIdsRange.values[0];
      const row4Texts = row4Range.values[0];
      
      let loadedCount = 0;
      
      for (let colIndex = 0; colIndex < columnIds.length && colIndex < row4Texts.length; colIndex++) {
        const columnId = columnIds[colIndex];
        const text = row4Texts[colIndex];
        
        if (columnId) {
          if (text !== null && text !== undefined && text.toString().trim()) {
            columnBackgroundInfo[columnId] = text.toString().trim();
            loadedCount++;
          } else {
            columnBackgroundInfo[columnId] = null;
          }
        }
      }
      
      localStorage.setItem("columnBackgroundInfo", JSON.stringify(columnBackgroundInfo));
      
      if (currentColumnId) {
        document.getElementById("background-text").value = columnBackgroundInfo[currentColumnId] || "";
      }
    });
  } catch (error) {
    debugError("Error loading background info from row 4:", error);
    appendMessage(`❌ Fehler beim Laden der Hintergrundinformationen aus Zeile 4: ${error.message}`, false);
  }
}

export async function exportBackgroundToRow4(showConfirmDialog) {
  showConfirmDialog(
    "Möchten Sie wirklich alle Hintergrundinformationen in eine neue Zeile 4 exportieren? Eine neue Zeile wird eingefügt.",
    () => performExportBackgroundToRow4()
  );
}

async function performExportBackgroundToRow4() {
  try {
    saveBackgroundInfo();

    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      
      const rangeToInsert = sheet.getRange("4:4");
      rangeToInsert.insert(Excel.InsertShiftDirection.down);
      
      const sourceRow = sheet.getRange("5:5");
      const targetRow = sheet.getRange("4:4");
      targetRow.copyFrom(sourceRow, Excel.RangeCopyType.formats);
      targetRow.format.fill.color = "#eeee8e";
      
      const columnIdsRange = sheet.getRangeByIndexes(0, 0, 1, 100);
      columnIdsRange.load("values");
      
      await context.sync();
      
      const columnIds = columnIdsRange.values[0];
      let exportedCount = 0;
      
      for (let colIndex = 0; colIndex < columnIds.length; colIndex++) {
        const columnId = columnIds[colIndex];
        
        if (columnId && columnBackgroundInfo[columnId]) {
          const cell = sheet.getRangeByIndexes(3, colIndex, 1, 1);
          cell.values = [[columnBackgroundInfo[columnId]]];
          exportedCount++;
        }
      }
      
      await context.sync();
    });
  } catch (error) {
    debugError("Error exporting background info to row 4:", error);
    appendMessage(`❌ Fehler beim Exportieren der Hintergrundinformationen in Zeile 4: ${error.message}`, false);
  }
}